// The React-owned module canvas (M1 Phase 4 — the shell cutover).
//
// Reads the placed modules from the store and renders one `<PlacedModule>` each,
// PORTALED into the legacy `#workspace` element (not `#react-root`). The portal
// keeps React's module nodes alongside the legacy `#welcome-screen`, the
// `#snap-indicator`, the drag ghost, the mobile placement line/spacers, and the
// workspace's `transform: scale` — everything the drag/scroll/placement math is
// relative to — untouched.
//
// Renders on BOTH platforms (PR 10): desktop positions modules absolutely from
// their grid coords; on mobile the modules array order IS the single-column
// stack order (mobile CSS overrides the inline geometry with `!important`), so
// rendering the array in order gives the stack. The legacy gesture engines
// (desktop drag/resize, mobile reorder/placement) stay in `main.js` and mutate
// the DOM transiently during a gesture, committing to the store on drop.

import { createPortal } from "react-dom";
import { useLayoutEffect, useState, useSyncExternalStore } from "react";
import { useBentoStore } from "../store/index.js";
import PlacedModule from "./PlacedModule.js";
import { buildRows, layoutRows, type Rect } from "../mobile/layout.js";
import { layoutItems, readMetrics, MOBILE_QUERY } from "../mobile/model.js";

// Phone layout (≤768px): every module is positioned from ../mobile/layout.ts —
// rows of one full-width module or two half-width "small" ones — the same
// geometry the phone drag engine (../mobile/engine.ts) previews, so a drop
// lands exactly where it was shown. Desktop keeps the grid coordinates.
// (No matchMedia — e.g. the jsdom test environment — means "not a phone".)
const hasMatchMedia = () => typeof window.matchMedia === "function";
function useIsMobile(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (!hasMatchMedia()) return () => {};
      const mq = window.matchMedia(MOBILE_QUERY);
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => hasMatchMedia() && window.matchMedia(MOBILE_QUERY).matches
  );
}

export default function Workspace() {
  const modules = useBentoStore((s) => s.modules);
  const isMobile = useIsMobile();
  const target = document.getElementById("workspace");

  // Re-lay out when the workspace width changes (rotation, resize).
  const [, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!target || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setWidth(target.clientWidth));
    ro.observe(target);
    return () => ro.disconnect();
  }, [target]);

  let rects: Map<string, Rect> | null = null;
  let height = 0;
  if (isMobile && target) {
    const items = layoutItems(modules);
    const h = new Map(items.map((it) => [it.id, it.h]));
    const out = layoutRows(buildRows(items), (id) => h.get(id) ?? 1.5, readMetrics(target));
    rects = out.rects;
    height = out.height;
  }

  // The workspace's own height on a phone (its modules are absolutely placed).
  useLayoutEffect(() => {
    if (!target) return;
    if (isMobile) target.style.setProperty("--m-ws-h", height + "px");
    else target.style.removeProperty("--m-ws-h");
  });

  if (!target) return null;
  return createPortal(
    modules.map((m) => (
      <PlacedModule key={m.id} module={m} mobileRect={rects?.get(m.id) ?? null} />
    )),
    target
  );
}
